import React, { useEffect, useState, useRef } from "react";
import '../custom.css';
import './CategoryMaster.css';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from 'primereact/inputtext';
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";
import moment from 'moment';
import * as XLSX from 'xlsx';

const BranchList = () => {
    const toast = useRef(null);
    const navigate = useNavigate();

    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        clientid: null,
    });

    const formatDate = (dateString) => {
        return moment(dateString).isValid()
            ? moment(dateString).format('DD-MM-YYYY')
            : '';
    };

    const exportToExcel = () => {
        const exportData = branches.map((row, i) => ({
            '#': i + 1,
            'Branch Name': row.branch_name || '',
            Title: row.branch_title || '',
            'Sl No': row.branch_slno || '',
            'Invoice Prefix': row.invoice_prefix || '',
            Phone: row.mobile || '',
            GSTIN: row.gstin_no || '',
            Address: row.address1 || '',
            'Effective From': formatDate(row.effective_from),
            'Data Transfer': row.data_transfer || '',
            Status: row.branch_status || ''
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Branches');
        XLSX.writeFile(wb, `BranchList_${moment().format('DDMMYYYY')}.xlsx`);
    };

    useEffect(() => {
        fetchClients();
        fetchBranches();
    }, []);

    const fetchClients = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation');
            setClients(res.data.map(c => ({ label: c.client_name, value: c.client_id })));
        } catch {
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load clients" });
        }
    };

    const fetchBranches = async (appliedFilters = {}) => {
        setLoading(true);
        try {
            let params = {};
            if (appliedFilters.clientid) {
                params.clientid = appliedFilters.clientid;
            }

            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/branches', { params });

            if (res.data && res.data.status === "success" && Array.isArray(res.data.branches)) {
                setBranches(res.data.branches);
            } else {
                setBranches([]);
            }
        } catch (err) {
            console.error("Error fetching branches:", err);
            setBranches([]);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load branches" });
        } finally {
            setLoading(false); //stop loading
        }
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };        

        setFilters(newFilters);
        fetchBranches(newFilters); //updated filters
    };

    const actionBodyTemplate = (rowData) => {
        return (
        <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-warning p-mr-2"
            onClick={() => navigate(`/branchedit/${rowData.branch_id}`)}
        />
        );
    };

    return (
        <div className="p-2">
            <Toast ref={toast} />
            <div className="masters-page-header">
                <h2>Branch List</h2>
                <p>View and manage branch records</p>
            </div>

            <div className="list-filter-bar">
                <div className="filter-field" style={{ flex: '1 1 200px', minWidth: '175px' }}>
                    <label htmlFor="client">Client</label>
                    <Dropdown id="client" value={filters.clientid} options={clients} onChange={(e) => handleFilterChange('clientid', e.value)} placeholder="Select Client" className="w-full" filter showClear />
                </div>
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-secondary"
                        tooltip="Reset Filters"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => {
                            setFilters({ clientid: null });
                            fetchBranches({});
                        }}
                    />
                </div>
                <div style={{ flex: '0 0 auto', alignSelf: 'flex-end' }}>
                    <Button icon="pi pi-file-excel" className="masters-excel-btn" tooltip="Export to Excel" tooltipOptions={{ position: 'top' }} onClick={exportToExcel} />
                </div>
                <div className="search-field filter-field">
                    <label>Search</label>
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-search"></i>
                        <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search branches..." className="p-inputtext-sm w-full" />
                    </span>
                </div>
            </div>

            <div className="card">
                <DataTable
                    value={branches}
                    showGridlines
                    paginator
                    rows={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm relative"
                    globalFilter={globalFilter}
                    loading={loading}
                    loadingIcon={
                        //custom overlay loader
                        <div className="flex align-items-center justify-content-center w-full h-full bg-black-alpha-30 absolute top-0 left-0">
                            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" fill="var(--surface-ground)" animationDuration=".7s" />
                        </div>
                    }
                    emptyMessage="No branches found"
                >
                    <Column field="branch_name" header="Branch Name" />
                    <Column field="branch_title" header="Title" />
                    <Column field="branch_slno" header="Sl No." />
                    <Column field="invoice_prefix" header="Invoice Prefix" />
                    <Column field="mobile" header="Phone" />
                    <Column field="gstin_no" header="GSTIN" />
                    <Column field="address1" header="Address" />
                    <Column field="effective_from" header="Effective From" body={(rowData) => formatDate(rowData.effective_from)} />
                    <Column
                        field="data_transfer"
                        header="Data Transfer"
                        body={(rowData) => <span className={`px-2 py-1 text-sm font-semibold border-round text-white ${rowData.data_transfer === 'updated' ? 'bg-green-500' : 'bg-orange-500'}`}>{rowData.data_transfer || '—'}</span>}
                    />
                    <Column
                        field="branch_status"
                        header="Status"
                        body={(rowData) => <span className={`px-2 py-1 text-sm font-semibold border-round text-white ${rowData.branch_status === 'Active' ? 'bg-green-500' : 'bg-orange-500'}`}>{rowData.branch_status}</span>}
                    />
                    <Column body={actionBodyTemplate} header="Edit"></Column>
                </DataTable>
            </div>
        </div>
    );
};

export default BranchList;
